import { generatePath } from 'react-router-dom'

import { FolderClient } from 'api/clients/FolderClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { LocationPicker, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { moveError } from 'components/NavRightClickMenu/components/MoveError/MoveError'
import { AlmanacFileSummary } from 'types'
import { useRootStore } from 'stores/RootStore'
import { useEvaluateMove } from 'components/LocationPicker/hooks/useEvaluateMove'
import { FOLDER_PATH } from 'paths'
import { showSuccessfulMoveToast } from 'common/ToastMessage/showMoveToast'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useMaximalAlmanacFile } from 'hooks/useMaximalAlmanacFile'

interface Props {
  file: AlmanacFileSummary
  dismiss?: () => void
  onSuccess?: () => void
}

export const MovePanel = ({ file, dismiss, onSuccess }: Props) => {
  const { handbooks } = useRootStore()
  const { evaluateMove } = useEvaluateMove()
  const { onMoveRequest } = useFinderContext()
  const { currentFolder } = useCurrentFolderStore()
  const { almanacFile } = useMaximalAlmanacFile(file.id)

  const handleMove = async (payload: { selectedFolder: SelectedFolder | null; newWorkspaceId: number }) => {
    const { selectedFolder, newWorkspaceId } = payload
    if (!selectedFolder) {
      // NOOP: documents cannot be moved to the root
      return
    }

    const dismissLoadingToast = showToastMessage('Moving...')
    const response = await FolderClient.bulkMoveFiles({
      folderIds: [],
      articleIds: [file.id],
      fileIds: [],
      destinationFolderId: selectedFolder.id,
      destinationWorkspaceId: newWorkspaceId,
      originalWorkspaceId: file.workspace.id,
    })
    dismissLoadingToast()

    if (response.type === 'error') {
      moveError({ status: response.status, data: response.data })
      return
    }

    const path = generatePath(FOLDER_PATH, { folderSlug: selectedFolder.seo_string })
    showSuccessfulMoveToast(1, path, selectedFolder.title)

    if (almanacFile) onMoveRequest([almanacFile], selectedFolder.id, newWorkspaceId)

    handbooks.refresh(currentFolder?.id)
    handbooks.refresh(selectedFolder.handbook_key)

    onSuccess?.()
  }

  return (
    <LocationPicker
      fileTitle={file.title}
      initialFolder={
        currentFolder
          ? {
              id: currentFolder.id,
              title: currentFolder.title,
              ancestor_keys: file.ancestors.map((f) => f.id),
            }
          : null
      }
      onSelectFolder={async (payload) => {
        dismiss?.()
        await evaluateMove({
          onSelectFolderPayload: payload,
          fileMoveAttemptPayload: {
            folderIds: [],
            articleIds: [file.id],
            originalWorkspaceId: file.workspace.id,
          },
          handleMove,
        })
      }}
      actionType="move"
      isRootDisabled
      disabledFolderIds={currentFolder ? [currentFolder.id] : []}
      keepIconInMobile
    />
  )
}
